import * as React from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TelegramIcon from '@mui/icons-material/Telegram';
import RedditIcon from '@mui/icons-material/Reddit';
import TwitterIcon from '@mui/icons-material/X';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import DiscordIcon from './DiscordIcon';
import MediumIcon from './MediumIcon';

function Copyright() {
  return (
    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
      {'Copyright © '}
      <Link color="text.secondary" href="https://vivachain.ai/">
        Viva AI
      </Link>
      &nbsp;
      {new Date().getFullYear()}
    </Typography>
  );
}

export default function Footer() {
  const [openDialog, setOpenDialog] = React.useState<null | 'privacy' | 'terms'>(null);

  const handleOpen = (type: 'privacy' | 'terms') => {
    setOpenDialog(type);
  };

  const handleClose = () => {
    setOpenDialog(null);
  };

  const privacyContent = (
    <>
      <DialogTitle>Privacy Policy</DialogTitle>
      <DialogContent dividers>
        <Typography 
          variant="subtitle2" 
          gutterBottom 
          sx={{ fontStyle: 'italic', mb: 3 }}>
          Last Updated (Effective Date): October 16, 2024
        </Typography>

        <Typography variant="h6" gutterBottom>1. Introduction</Typography>
        <Typography variant="body1" paragraph>
          Welcome to Viva AI (“we,” “us,” or “our”). We respect your privacy and are committed
          to protecting it through our compliance with this Privacy Policy. This Policy explains
          how we collect, use, disclose, and safeguard your information when you use our website,
          products, and services (collectively, the “Services”).
        </Typography>

        <Typography variant="h6" gutterBottom>2. Information We Collect</Typography>
        <Typography variant="body1" paragraph>
          • Personal Information: We may collect information that can identify you directly or indirectly,
          such as your name, email address, company name, and job title, when you register for an account,
          request a demo, or contact us.
        </Typography>
        <Typography variant="body1" paragraph>
          • Usage Data: We automatically collect certain information about your interactions with the Services,
          including your IP address, browser type, device information, pages visited, and the duration of
          your visits.
        </Typography>
        <Typography variant="body1" paragraph>
          • Cookies & Similar Technologies: We use cookies, beacons, and other tracking technologies to improve
          your experience, analyze usage, and deliver personalized content. You can control the use of cookies
          at the browser level.
        </Typography>

        <Typography variant="h6" gutterBottom>3. How We Use Your Information</Typography>
        <Typography variant="body1" paragraph>
          We may use the information we collect for various purposes, including:
        </Typography>
        <Typography variant="body1" paragraph>
          • To provide, maintain, and improve our Services.
        </Typography>
        <Typography variant="body1" paragraph>
          • To personalize your experience and deliver content relevant to your interests.
        </Typography>
        <Typography variant="body1" paragraph>
          • To communicate with you about updates, promotions, and events.
        </Typography>
        <Typography variant="body1" paragraph>
          • To analyze usage patterns and improve our website and product offerings.
        </Typography>
        <Typography variant="body1" paragraph>
          • To comply with applicable laws and regulations, or as otherwise required by law.
        </Typography>

        <Typography variant="h6" gutterBottom>4. How We Share Your Information</Typography>
        <Typography variant="body1" paragraph>
          We may share your information with:
        </Typography>
        <Typography variant="body1" paragraph>
          • Service Providers: Trusted third-party vendors who perform services on our behalf, such as hosting,
          analytics, or email marketing.
        </Typography>
        <Typography variant="body1" paragraph>
          • Business Transfers: In the event of a merger, acquisition, or asset sale, your information may be
          transferred to a successor entity.
        </Typography>
        <Typography variant="body1" paragraph>
          • Legal Requirements: We may disclose your information if required by law or to protect our rights,
          property, or safety.
        </Typography>

        <Typography variant="h6" gutterBottom>5. Data Security</Typography>
        <Typography variant="body1" paragraph>
          We implement industry-standard security measures designed to protect your information from unauthorized
          access, disclosure, alteration, or destruction. However, no method of transmission over the internet
          or electronic storage is completely secure, and we cannot guarantee absolute security.
        </Typography>

        <Typography variant="h6" gutterBottom>6. International Transfers</Typography>
        <Typography variant="body1" paragraph>
          Your information may be transferred to and stored on servers located outside your country of residence.
          We take steps to ensure that data transfers comply with applicable data protection laws.
        </Typography>

        <Typography variant="h6" gutterBottom>7. Your Rights & Choices</Typography>
        <Typography variant="body1" paragraph>
          • Opt-Out: You may opt out of certain communications or request deletion of your account by contacting us.
        </Typography>
        <Typography variant="body1" paragraph>
          • Access & Correction: You may request access to, correction of, or deletion of your personal information,
          subject to certain legal exceptions.
        </Typography>

        <Typography variant="h6" gutterBottom>8. Third-Party Links</Typography>
        <Typography variant="body1" paragraph>
          Our Services may contain links to third-party websites. We are not responsible for the privacy practices
          or content of those sites.
        </Typography>

        <Typography variant="h6" gutterBottom>9. Children’s Privacy</Typography>
        <Typography variant="body1" paragraph>
          Our Services are not directed at children under the age of 16. We do not knowingly collect personal
          information from children under 16.
        </Typography>

        <Typography variant="h6" gutterBottom>10. Changes to This Policy</Typography>
        <Typography variant="body1" paragraph>
          We may update this Privacy Policy from time to time. Any changes will be posted on this page with an
          updated “Last Updated” date. Your continued use of the Services after any changes constitutes acceptance
          of those changes.
        </Typography>

        <Typography variant="h6" gutterBottom>11. Contact Us</Typography>
        <Typography variant="body1" paragraph>
          If you have any questions about this Privacy Policy, please contact us at: support@vivachain.ai
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </>
  );

  const termsContent = (
    <>
      <DialogTitle>Terms of Service</DialogTitle>
      <DialogContent dividers>
        <Typography 
          variant="subtitle2" 
          gutterBottom 
          sx={{ fontStyle: 'italic', mb: 3 }}>
          Last Updated (Effective Date): October 16, 2024
        </Typography>

        <Typography variant="h6" gutterBottom>1. Acceptance of Terms</Typography>
        <Typography variant="body1" paragraph>
          By accessing or using Viva AI’s website, products, and services (the “Services”), you agree to be bound by these
          Terms of Service (“Terms”) and our Privacy Policy. If you do not agree, do not access or use the Services.
        </Typography>

        <Typography variant="h6" gutterBottom>2. Eligibility</Typography>
        <Typography variant="body1" paragraph>
          You must be at least 16 years old to use the Services. By using the Services, you represent that you meet this age requirement.
        </Typography>

        <Typography variant="h6" gutterBottom>3. Accounts & Security</Typography>
        <Typography variant="body1" paragraph>
          To access certain features, you may be required to create an account. You agree to provide accurate, current, and complete information
          and to maintain the security of your password. You are responsible for all activities that occur under your account.
        </Typography>

        <Typography variant="h6" gutterBottom>4. User Conduct</Typography>
        <Typography variant="body1" paragraph>
          You agree not to:
        </Typography>
        <Typography variant="body1" paragraph>
          • Use the Services for any unlawful, harmful, or fraudulent purpose.
        </Typography>
        <Typography variant="body1" paragraph>
          • Interfere with or disrupt the Services, servers, or networks connected to the Services.
        </Typography>
        <Typography variant="body1" paragraph>
          • Upload or transmit viruses, malware, or harmful code.
        </Typography>
        <Typography variant="body1" paragraph>
          • Reverse engineer, decompile, or extract source code from the Services without our permission.
        </Typography>

        <Typography variant="h6" gutterBottom>5. Intellectual Property</Typography>
        <Typography variant="body1" paragraph>
          All content, features, and functionality (including text, graphics, logos, software, and trademarks) are owned by or licensed to us and
          are protected by intellectual property laws. You may not reproduce, distribute, or create derivative works without our prior written permission.
        </Typography>

        <Typography variant="h6" gutterBottom>6. Fees & Payment</Typography>
        <Typography variant="body1" paragraph>
          Certain features may require payment. Any fees and payment terms will be posted on our website or provided at the time of purchase.
          You agree to pay all applicable fees and taxes in a timely manner.
        </Typography>

        <Typography variant="h6" gutterBottom>7. Disclaimer of Warranties</Typography>
        <Typography variant="body1" paragraph>
          The Services are provided “as is” and “as available” without warranties of any kind, whether express or implied. We disclaim all warranties,
          including implied warranties of merchantability, fitness for a particular purpose, and non-infringement.
        </Typography>

        <Typography variant="h6" gutterBottom>8. Limitation of Liability</Typography>
        <Typography variant="body1" paragraph>
          To the fullest extent permitted by law, we shall not be liable for any indirect, incidental, special, consequential, or punitive damages,
          including loss of profits or data, arising out of your use or inability to use the Services, even if advised of the possibility of such damages.
        </Typography>

        <Typography variant="h6" gutterBottom>9. Indemnification</Typography>
        <Typography variant="body1" paragraph>
          You agree to defend, indemnify, and hold us harmless from and against any claims, damages, liabilities, and expenses arising out of or related
          to your use of the Services, your violation of these Terms, or your infringement of any rights of another.
        </Typography>

        <Typography variant="h6" gutterBottom>10. Termination</Typography>
        <Typography variant="body1" paragraph>
          We may terminate or suspend your access to the Services at any time, without notice, for any reason. Upon termination, your right to use the
          Services will immediately cease.
        </Typography>

        <Typography variant="h6" gutterBottom>11. Governing Law & Dispute Resolution</Typography>
        <Typography variant="body1" paragraph>
          These Terms shall be governed by and construed in accordance with the laws of [Your Jurisdiction], without regard to its conflict of law principles.
          Any disputes shall be resolved in the state or federal courts located in [Your Jurisdiction], and you consent to personal jurisdiction and venue therein.
        </Typography>

        <Typography variant="h6" gutterBottom>12. Changes to the Terms</Typography>
        <Typography variant="body1" paragraph>
          We may update these Terms from time to time. Any changes will be posted with a revised “Last Updated” date.
          Your continued use of the Services after such changes constitute acceptance of the updated Terms.
        </Typography>

        <Typography variant="h6" gutterBottom>13. Contact Us</Typography>
        <Typography variant="body1" paragraph>
          If you have any questions about these Terms, please contact us at: support@vivachain.ai
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </>
  );

  return (
    <Container
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: { xs: 4, sm: 8 },
        py: { xs: 8, sm: 10 },
        textAlign: { sm: 'center', md: 'left' },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <div>
          <Link
            color="text.secondary"
            variant="body2"
            onClick={() => handleOpen('privacy')}
            sx={{ cursor: 'pointer' }}
          >
            Privacy Policy
          </Link>
          <Typography sx={{ display: 'inline', mx: 0.5, opacity: 0.5 }}>
            &nbsp;•&nbsp;
          </Typography>
          <Link
            color="text.secondary"
            variant="body2"
            onClick={() => handleOpen('terms')}
            sx={{ cursor: 'pointer' }}
          >
            Terms of Service
          </Link>
          <Copyright />
        </div>
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          sx={{ justifyContent: 'left', color: 'text.secondary' }}
        >
          {/* <IconButton
            color="inherit"
            size="small"
            href="https://discord.gg/umdwrhqRGP"
            aria-label="Discord"
            sx={{ alignSelf: 'center' }}
          >
            <DiscordIcon />
          </IconButton> */}
          <IconButton
            color="inherit"
            size="small"
            href="https://t.me/vivaaiofficial"
            aria-label="Telegram"
            sx={{ alignSelf: 'center' }}
          >
            <TelegramIcon />
          </IconButton>
          <IconButton
            color="inherit"
            size="small"
            href="https://x.com/VivaAIOfficial"
            aria-label="X"
            sx={{ alignSelf: 'center' }}
          >
            <TwitterIcon />
          </IconButton>
          <IconButton
            color="inherit"
            size="small"
            href="https://medium.com/@vivaai"
            aria-label="Medium"
            sx={{ alignSelf: 'center' }}
          >
            <MediumIcon />
          </IconButton>
          <IconButton
            color="inherit"
            size="small"
            href="https://www.reddit.com/r/vivachain/"
            aria-label="Reddit"
            sx={{ alignSelf: 'center' }}
          >
            <RedditIcon />
          </IconButton>
        </Stack>
      </Box>

      {/* Dialog for Privacy Policy or Terms of Service */}
      <Dialog
        open={openDialog !== null}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
      >
        {openDialog === 'privacy' && privacyContent}
        {openDialog === 'terms' && termsContent}
      </Dialog>
    </Container>
  );
}